import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Label from 'LuxKit/Label/Label'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import type { ComponentProps } from 'react'
import React, { useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import LuxPlusLogo from './Logos/LuxPlusLogo'
import LuxPlusBookingProtectionLabelLabel from './LuxPlusBookingProtectionLabel'
import LuxPlusInsuranceLabel from './LuxPlusInsuranceLabel'

const LOGO_HEIGHT = 10

interface Props extends Pick<ComponentProps<typeof Clickable>, 'to'>, Utils.PropsWithDataAttributes {
  onClick?: () => void
  type: App.LuxPlusLabelType
  saveAmount?: number
  hideSaveAmountPlus?: boolean
  currency?: string
  description?: string
}

function LuxPlusLabel(props: Props) {
  const {
    currency,
    description,
    type,
    onClick,
    saveAmount,
    hideSaveAmountPlus,
    to,
    ...rest
  } = props

  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const clickHandler = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return
    e.preventDefault()
    e.stopPropagation()
    onClick()
  }, [onClick])

  if (!luxPlusEnabled) return null

  const saveAmountSuffix = hideSaveAmountPlus ? '' : '+'

  const label = <Label
    data-testid="lux-plus-label"
    {...rest}
    kind="primary"
    variant="lux-plus"
    endIcon={onClick ? <LineInfoCircleIcon /> : undefined}
  >
    {type === 'bonus-inclusions' && !description && <FormattedMessage
      id="OXS7m77/WN"
      defaultMessage="Bonus inclusions with {logo}"
      description="Label for bonus inclusions available with LuxPlus+ membership"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'bonus-inclusions' && !!description && <FormattedMessage
      id="31PC5vk6YX"
      defaultMessage="{description} with {logo}"
      description="Label for custom bonus inclusions description with LuxPlus+ logo"
      values={{ description, logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member' && <FormattedMessage
      id="txcDQVltNp"
      defaultMessage="{logo} member"
      description="Label indicating LuxPlus+ membership status"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'only-logo' && <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />}
    {type === 'early-access-offer' && <FormattedMessage
      id="SmkL8grGGW"
      defaultMessage="Early access with {logo}"
      description="Label for early access offer available with LuxPlus+ membership"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member-only-offer' && <FormattedMessage
      id="JrNCTRM29G"
      defaultMessage="{logo} member-only offer"
      description="Label for offer exclusively available to LuxPlus+ members"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'member-price-offer' && !!saveAmount && <FormattedMessage
      id="yG8+sK8jiK"
      defaultMessage="Save {formattedSaveAmount} with {logo}"
      description="Label showing LuxPlus+ member savings amount on an offer"
      values={{
        formattedSaveAmount: <><FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix}</>,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'member-price-offer-lpp' && !!saveAmount && <FormattedMessage
      id="ZgpitutO8u"
      defaultMessage="Save {formattedSaveAmount} with {logo}"
      description="Label showing LuxPlus+ member savings amount on a bedbank offer"
      values={{
        formattedSaveAmount: <><FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix}</>,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'member-price-offer-tour' && !!saveAmount && <FormattedMessage
      id="5zSuQiiJi/"
      defaultMessage="Save {formattedSaveAmount}/person with {logo}"
      description="Label showing LuxPlus+ per-person member savings amount on a tour"
      values={{
        formattedSaveAmount: <><FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix}</>,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'options-available-tour' && <FormattedMessage
      id="XAJJIOTUNB"
      defaultMessage="{logo} options available"
      description="Label indicating LuxPlus+ pricing options are available for a tour"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'plus' && <LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} />}
    {type === 'plus-preview' && <FormattedMessage
      id="S951VAWukZ"
      defaultMessage="{logo} preview"
      description="Label for LuxPlus+ free preview mode"
      values={{ logo: <LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} /> }}
    />}
    {type === 'insurance' && !!saveAmount && <LuxPlusInsuranceLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
    {type === 'booking-protection' && !!saveAmount && <LuxPlusBookingProtectionLabelLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
    {type === 'savings' && !!saveAmount && <FormattedMessage
      id="3aEGrp8JsL"
      defaultMessage="Extra {formattedSaveAmount} saved with {logo}"
      description="Label showing extra savings achieved with LuxPlus+ membership"
      values={{
        formattedSaveAmount: <FormatCurrency format="roundedDollar" value={saveAmount} />,
        logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
      }}
    />}
    {type === 'member-inclusions' && <FormattedMessage
      id="6zoPC0xA1+"
      defaultMessage="Member inclusions with {logo}"
      description="Label for member inclusions available with LuxPlus+ membership"
      values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
    />}
  </Label>

  if (onClick || to) return <Clickable onClick={clickHandler} to={to} data-testid="lux-plus-label-clickable">{label}</Clickable>

  return label
}

export default LuxPlusLabel
